
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import { handleError } from '@/utils/handleError';
import { codedError, extractFunctionError } from "@/utils/errorUtils";
import { Consent, ConsentInitiationResult, ConsentInitiationStatus } from "@/types/openpayments/consent";
import type { AccountType } from "@/types/accounts";

export interface InitiateConsentCreationOptions {
  bic: string;
  sameDevice?: boolean;
  ref?: string;
  countryCode?: string;
  forceNew?: boolean;
  /** FIN-1567: which login to request -- absent means business, matching consent-api's own
   *  default. Renewal passes the account's stored type; a fresh connect passes the user's
   *  AccountTypeCards pick (or nothing, at a one-login bank). */
  accountType?: AccountType;
}

// caller-access-context.EC5/AC3 (issue #616) — see accountApiClient.ts for the rationale.
const CALLER_ACCESS_MODE_HEADERS = { "x-caller-access-mode": "user-present" } as const;

/**
 * Client for interacting with the consent-api edge function
 */
export class ConsentApiClient {
  /**
   * Initiate consent creation for a bank account
   * @returns Promise with enhanced initiation details
   */
  static async initiateConsentCreation({ bic, sameDevice, ref, countryCode, forceNew, accountType }: InitiateConsentCreationOptions): Promise<ConsentInitiationResult> {
    try {
      const callbackUrl = `${window.location.origin}/bank`
      const { data, error } = await supabase.functions.invoke('consent-api', {
        method: 'POST',
        // `ref` names which institution was picked when several share a bic (FIN-592);
        // `countryCode` identifies a bic-less bank, whose bic is the empty string (FIN-783).
        body: {
          action: 'initiateConsentCreation',
          bic,
          sameDevice,
          callbackUrl,
          ...(ref ? { ref } : {}),
          ...(countryCode ? { countryCode } : {}),
          // Skip reusing a valid consent and go back to the bank (FIN-863).
          ...(forceNew ? { forceNew: true } : {}),
          ...(accountType ? { accountType } : {}),
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) throw error;

      toast.success(translateOutsideReact('consultantToasts.api.consentCreationInitiated'));
      return data;
    } catch (error) {
      const functionError = await extractFunctionError(error);
      if (functionError?.error === 'KYC_REQUIRED') {
        throw codedError('KYC_REQUIRED', functionError.details);
      }
      // `functionError` above already consumed `error.context`'s body via extractFunctionError()
      // (a Response can only be `.json()`'d once) — forward it as preParsedBody so handleError()'s
      // own extractor doesn't see `context.bodyUsed === true` and silently fall back to the fixed
      // `fallback` string instead of the backend's real (sanitized) message. Same pattern as
      // accountApiClient.ts's getBankAccountsToConnect.
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToInitiateConsentCreation'), 'initiateConsentCreation', functionError);
      throw error;
    }
  }
  
  /**
   * Get a specific consent by ID
   * @param consentId Consent ID
   * @param bic Bank BIC code (required)
   * @returns Promise with the consent
   */
  static async getConsent(consentId: string, bic: string): Promise<Consent> {
    try {
      const { data, error } = await supabase.functions.invoke('consent-api', {
        method: 'POST',
        body: { action: 'getConsent', consentId, bic },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetBankAccountConsent'), 'getConsent');
      throw error;
    }
  }
  
  /**
   * Get consent initiation status and create DB consent when completed
   * @param consentId Consent ID
   * @param authorisationId Authorization ID (optional for existing consents)
   * @param bic Bank BIC code (required)
   * @returns Promise with enhanced initiation status
   */
  static async getConsentInitiationStatus(consentId: string, authorisationId: string | null, bic: string, ref?: string, countryCode?: string): Promise<ConsentInitiationStatus> {
    try {
      const { data, error } = await supabase.functions.invoke('consent-api', {
        method: 'POST',
        body: {
          action: 'getConsentInitiationStatus',
          consentId,
          authorisationId,
          bic,
          ...(ref ? { ref } : {}),
          ...(countryCode ? { countryCode } : {}),
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetConsentInitiationStatus'), 'getConsentInitiationStatus');
      throw error;
    }
  }
  
  /**
   * Refresh a consent by creating a new one
   * @param consentId Consent ID to refresh
   * @returns Promise with the new consent
   */
  static async refreshConsent(consentId: string): Promise<Consent> {
    try {
      const { data, error } = await supabase.functions.invoke('consent-api', {
        method: 'POST',
        body: { action: 'refreshConsent', consentId },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) throw error;

      toast.success(translateOutsideReact('consultantToasts.api.consentRefreshed'));
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToRefreshBankAccountConsent'), 'refreshConsent');
      throw error;
    }
  }
}
